import React, { useContext, useEffect, useState } from "react";
import {
  Container,
  Table,
  Badge,
  Button,
  Card,
  Spinner,
} from "react-bootstrap";
import { MessageSquare, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminMasterLayout from "../../../layout/admin/AdminMasterLayout";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import Swal from "sweetalert2";
import "../../../assets/css/admin/supportticket/supportTicketInAdmin.css";

const SupportTicketInAdmin = () => {
  const [tickets, setTickets] = useState([]);
  const [resolvingId, setResolvingId] = useState(null);

  const { baseUrl } = useContext(AppContext);
  const navigate = useNavigate();

  // ================= FETCH ALL TICKETS =================

  const fetchAllTickets = async () => {
    try {
      const res = await axios.get(
        `${baseUrl}/admin/helpsupport/fetchAllTickets`,
      );

      if (res.data.success === true) {
        setTickets(res.data.tickets || []);
      }
    } catch (err) {
      console.log(err.response?.data || err);
    }
  };

  // ================= RESOLVE TICKET =================

  const resolveTicket = async (ticketId) => {
    const result = await Swal.fire({
      title: "Resolve Ticket?",
      text: "Are you sure this issue has been solved?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Resolve",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setResolvingId(ticketId);

      const formData = new FormData();
      formData.append("id", ticketId);

      const res = await axios.post(
        `${baseUrl}/admin/helpsupport/resolveTicket`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res.data.success === true) {
        // IMPORTANT: lowercase
        setTickets((prev) =>
          prev.map((ticket) =>
            ticket.id === ticketId ? { ...ticket, status: "resolved" } : ticket,
          ),
        );

        Swal.fire({
          icon: "success",
          title: "Resolved",
          text: "Ticket has been resolved successfully.",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.log(err.response?.data || err);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.message || "Unable to resolve ticket.",
      });
    } finally {
      setResolvingId(null);
    }
  };

  // ================= STATUS CLASS =================

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return "status-open";

      case "in_progress":
        return "status-in-progress";

      case "resolved":
        return "status-resolved";

      default:
        return "";
    }
  };

  // ================= STATUS TEXT =================

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return "Open";

      case "in_progress":
        return "In Progress";

      case "resolved":
        return "Resolved";

      default:
        return status || "Unknown";
    }
  };

  useEffect(() => {
    fetchAllTickets();
  }, []);

  return (
    <AdminMasterLayout>
      <Container fluid className="support-ticket-wrapper">
        <h3 className="support-ticket-heading">Support Tickets</h3>

        <Card className="support-ticket-card">
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>Ticket ID</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>From</th>
                  <th>Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {tickets.length > 0 ? (
                  tickets.map((ticket) => {
                    const status = ticket.status?.toLowerCase();

                    return (
                      <tr key={ticket.id}>
                        <td className="ticket-id">{ticket.id}</td>

                        <td className="ticket-title">{ticket.title}</td>

                        <td className="ticket-description">
                          {ticket.description}
                        </td>

                        <td>
                          <Badge
                            className={
                              ticket.generated_by === "shop"
                                ? "from-badge seller-badge"
                                : "from-badge customer-badge"
                            }
                          >
                            {ticket.generated_by === "shop"
                              ? "Seller"
                              : "Customer"}
                          </Badge>
                        </td>

                        <td>
                          <Badge
                            className={`status-badge ${getStatusClass(status)}`}
                          >
                            {getStatusText(status)}
                          </Badge>
                        </td>

                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            {/* CHAT */}
                            <Button
                              className="chat-btn"
                              onClick={() =>
                                navigate(
                                  `/admin-support-ticket-chat/${ticket.id}`,
                                )
                              }
                              title="Open Chat"
                            >
                              <MessageSquare size={15} />
                            </Button>

                            {/* RESOLVE */}
                            <Button
                              className="resolve-btn"
                              disabled={
                                status === "resolved" ||
                                resolvingId === ticket.id
                              }
                              onClick={() => resolveTicket(ticket.id)}
                              title="Mark as Resolved"
                            >
                              {resolvingId === ticket.id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <CheckCircle size={15} />
                              )}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      No support tickets found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      </Container>
    </AdminMasterLayout>
  );
};

export default SupportTicketInAdmin;
